import axios from "axios";
import { useAuth } from "@/store/authStore";

export const API_URL = __IS_DEV__ ? "/api" : `${__API_URL__}/api`;

const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const { accessToken } = useAuth.getState();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

let isRefreshing = false;
let queued: Array<(token: string) => void> = [];

function queueRequest(cb: (token: string) => void) {
  queued.push(cb);
}

function resolveQueue(token: string) {
  for (const cb of queued) cb(token);
  queued = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (originalRequest?._retry) {
      return Promise.reject(error);
    }

    const store = useAuth.getState();
    const { refreshToken } = store;

    if (!refreshToken) {
      store.logout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        queueRequest((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          resolve(api(originalRequest));
        });
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const resp = await axios.post(
        `${API_URL}/users/auth/token/refresh/`,
        { refresh: refreshToken },
        { withCredentials: false }
      );

      const newAccess = resp.data.access;

      useAuth.getState().setAccessToken(newAccess);
      resolveQueue(newAccess);

      originalRequest.headers.Authorization = `Bearer ${newAccess}`;
      return api(originalRequest);
    } catch (refreshErr) {
      useAuth.getState().logout();
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
